import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Content, ContentFormData } from '../../../types/content';
import { Language } from '../../../types/content';
import { DaisyUIMediaLibrary as MediaLibraryComponent } from './DaisyUIMediaLibrary';
import { useSession } from '~/components/contexts/SessionProvider';

interface ContentEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  content: Content | null;
  onSave: (content: ContentFormData) => void;
  isNew?: boolean;
  isSaving?: boolean;
}

export const ContentEditorDialog = ({
  isOpen,
  onClose,
  content,
  onSave,
  isNew = false,
  isSaving = false,
}: ContentEditorDialogProps) => {
  const { t } = useTranslation('admin');
  const { owner } = useSession();
  const quillRef = useRef<ReactQuill>(null);

  // Get the first available agent ID from the user's agents
  const agentId = owner?.agents?.[0]?._id || '';

  // Form state
  const [formData, setFormData] = useState<ContentFormData>({
    type: 'blog',
    title: { en: '', es: '' },
    description: { en: '', es: '' },
    content: { en: '', es: '' },
    slug: { en: '', es: '' },
    author: '',
    thumbnail: '',
    tags: { en: [], es: [] },
    featured: false,
    status: 'draft',
    seo: {
      metaTitle: { en: '', es: '' },
      metaDescription: { en: '', es: '' },
      canonicalUrl: { en: '', es: '' },
      ogImage: '',
      ogType: 'article',
      keywords: { en: [], es: [] },
      schema: { en: {}, es: {} },
    },
  });

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    Language.EN,
  );
  const [activeTab, setActiveTab] = useState<'content' | 'meta' | 'seo'>(
    'content',
  );
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [mediaLibraryContext, setMediaLibraryContext] = useState<
    'content' | 'thumbnail'
  >('thumbnail');
  // Media upload function for Quill editor
  const handleImageInsert = useCallback(
    async (file: File) => {
      if (!file || !agentId) return;

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('agent_id', agentId);

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/files/upload`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: formData,
          },
        );

        if (response.ok) {
          const result = await response.json();
          const imageUrl = `${import.meta.env.VITE_API_URL}/media/${
            result.data.url
          }`;

          // Insert image into Quill editor
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection();
            quill.insertEmbed(range?.index || 0, 'image', imageUrl);
          }
        } else {
          console.error('Failed to upload image');
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    },
    [agentId],
  );

  // Enhanced Quill modules configuration with comprehensive toolbar
  const stableModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link', 'image'],
          ['blockquote', 'code-block'],
          [{ align: [] }],
          ['clean'],
        ],
        handlers: {
          image: () => {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/*');
            input.click();

            input.onchange = async () => {
              const file = input.files?.[0];
              if (file) {
                await handleImageInsert(file);
              }
            };
          },
        },
      },
      keyboard: {
        bindings: {
          tab: {
            key: 9,
            handler: function () {
              return true;
            },
          },
        },
      },
    }),
    [handleImageInsert],
  );

  const stableFormats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'blockquote',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'color',
      'background',
      'align',
      'code-block',
    ],
    [],
  );

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        type: 'blog',
        title: { en: '', es: '' },
        description: { en: '', es: '' },
        content: { en: '', es: '' },
        slug: { en: '', es: '' },
        author: '',
        thumbnail: '',
        tags: { en: [], es: [] },
        featured: false,
        status: 'draft',
        seo: {
          metaTitle: { en: '', es: '' },
          metaDescription: { en: '', es: '' },
          canonicalUrl: { en: '', es: '' },
          ogImage: '',
          ogType: 'article',
          keywords: { en: [], es: [] },
          schema: { en: {}, es: {} },
        },
      });
      setSelectedLanguage(Language.EN);
      setActiveTab('content');
    }
  }, [isOpen]);

  // Initialize form data when content changes
  useEffect(() => {
    if (content && isOpen) {
      // Helper function to ensure we have both language keys
      const ensureLanguageObject = (obj: any, defaultValue: any = '') => {
        if (!obj || typeof obj !== 'object') {
          return { en: defaultValue, es: defaultValue };
        }
        return {
          en: obj.en || defaultValue,
          es: obj.es || defaultValue,
        };
      };

      // Helper function for arrays
      const ensureLanguageArray = (obj: any) => {
        if (!obj || typeof obj !== 'object') {
          return { en: [], es: [] };
        }
        return {
          en: Array.isArray(obj.en) ? obj.en : [],
          es: Array.isArray(obj.es) ? obj.es : [],
        };
      };

      setFormData({
        type: content.type,
        title: ensureLanguageObject(content.metadata?.title),
        description: ensureLanguageObject(content.metadata?.description),
        content: content.translations?.reduce(
          (acc, trans) => {
            (acc as any)[trans.language] = trans.content || '';
            return acc;
          },
          { en: '', es: '' } as Record<string, string>,
        ) || { en: '', es: '' },
        slug: ensureLanguageObject(content.metadata?.slug),
        author: content.metadata?.author || '',
        thumbnail: content.metadata?.thumbnail || '',
        tags: ensureLanguageArray(content.metadata?.tags),
        featured: content.metadata?.featured || false,
        status: content.status,
        seo: {
          metaTitle: ensureLanguageObject(content.seo?.metaTitle),
          metaDescription: ensureLanguageObject(content.seo?.metaDescription),
          canonicalUrl: ensureLanguageObject(content.seo?.canonicalUrl),
          ogImage: content.seo?.ogImage || '',
          ogType: content.seo?.ogType || 'article',
          keywords: ensureLanguageArray(content.seo?.keywords),
          schema: ensureLanguageObject(content.seo?.schema),
        },
      });
    }
  }, [content, isOpen]);

  // Auto-generate slug from title
  const generateSlug = useCallback((title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }, []);

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof ContentFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [agentId],
  );

  const handleLanguageFieldChange = useCallback(
    (field: string, language: string, value: string) => {
      setFormData((prev) => {
        const currentFieldValue = prev[field as keyof ContentFormData];
        const newFieldValue =
          typeof currentFieldValue === 'object' && currentFieldValue !== null
            ? { ...currentFieldValue, [language]: value }
            : { [language]: value };

        return {
          ...prev,
          [field]: newFieldValue,
        };
      });

      // Auto-generate slug when title changes
      if (field === 'title' && value.trim()) {
        const slug = generateSlug(value);
        setFormData((prev) => ({
          ...prev,
          slug: {
            ...prev.slug,
            [language]: slug,
          },
        }));
      }
    },
    [generateSlug],
  );

  const handleContentChange = useCallback(
    (value: string) => {
      setFormData((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          [selectedLanguage]: value,
        },
      }));
    },
    [selectedLanguage],
  );

  const handleSave = useCallback(() => {
    onSave(formData);
  }, [formData, onSave]);

  // Media upload functions
  const handleThumbnailUpload = useCallback(
    async (file: File) => {
      if (!file || !agentId) return;

      setIsUploadingThumbnail(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('agent_id', agentId); // Use a default agent ID for admin uploads

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/files/upload`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
            body: formData,
          },
        );

        if (response.ok) {
          const result = await response.json();
          const thumbnailUrl = `${import.meta.env.VITE_API_URL}/media/${
            result.data.url
          }`;
          handleFieldChange('thumbnail', thumbnailUrl);
        } else {
          console.error('Failed to upload thumbnail');
        }
      } catch (error) {
        console.error('Upload error:', error);
      } finally {
        setIsUploadingThumbnail(false);
      }
    },
    [agentId, handleFieldChange],
  );

  const handleMediaSelect = useCallback(
    (media: any) => {
      const mediaUrl = `${import.meta.env.VITE_API_URL}/media/${media.url}`;

      if (mediaLibraryContext === 'content') {
        // Insert into content editor
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selection = quill.getSelection();
          const index = selection ? selection.index : quill.getLength();
          quill.insertEmbed(index, 'image', mediaUrl);

          // Set focus back to editor after insertion
          setTimeout(() => {
            quill.setSelection(index + 1, 0);
          }, 100);
        }
      } else {
        // Set as thumbnail
        handleFieldChange('thumbnail', mediaUrl);
      }

      setShowMediaLibrary(false);
    },
    [handleFieldChange, mediaLibraryContext],
  );

  if (!isOpen) return null;

  // If no valid agent ID is available, show an error message
  if (!agentId) {
    return (
      <div className="modal modal-open">
        <div className="modal-box">
          <h3 className="text-lg font-bold">⚠️ No Agent Available</h3>
          <p className="py-4">
            You need at least one agent to upload media. Please create an agent
            first.
          </p>
          <div className="modal-action">
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal modal-open">
      <div className="m-0 flex h-screen w-screen flex-col rounded-none p-0">
        {/* Header */}
        <div className="border-base-300 bg-base-100 flex items-center justify-between border-b px-6 py-3 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
              <span className="text-primary text-lg">
                {isNew ? '✏️' : '📝'}
              </span>
            </div>
            <div>
              <h2 className="text-base-content text-xl font-bold">
                {isNew ? 'Create New Content' : 'Edit Content'}
              </h2>
              <p className="text-base-content/60 text-sm">
                {formData.type === 'blog' ? 'Blog Post' : 'Documentation'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
            disabled={isSaving}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Main Content Area */}
        <div className="flex min-h-0 flex-1 overflow-y-auto">
          {/* Left Panel - Content Editor */}
          <div className="bg-base-100 flex min-w-0 flex-1 flex-col">
            {/* Language Tabs */}
            <div className="border-base-300 flex border-b bg-gray-50 px-6 pb-0 pt-4">
              <div className="tabs tabs-bordered">
                <button
                  className={`tab tab-lg font-medium ${
                    selectedLanguage === Language.EN ? 'tab-active' : ''
                  }`}
                  onClick={() => setSelectedLanguage(Language.EN)}
                >
                  <span className="mr-2">🇺🇸</span>
                  English
                </button>
                <button
                  className={`tab tab-lg font-medium ${
                    selectedLanguage === Language.ES ? 'tab-active' : ''
                  }`}
                  onClick={() => setSelectedLanguage(Language.ES)}
                >
                  <span className="mr-2">🇪🇸</span>
                  Español
                </button>
              </div>
            </div>

            {/* Content Form */}
            <div className="flex min-h-0 flex-1 flex-col overflow-auto p-6">
              {/* Title and Description Row */}
              <div className="mb-6 grid grid-cols-2 gap-6">
                {/* Title Input */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-base font-semibold">
                      Title ({selectedLanguage.toUpperCase()})
                    </span>
                    <span className="label-text-alt text-error font-medium">
                      {!formData.title[selectedLanguage]?.trim()
                        ? 'Required'
                        : ''}
                    </span>
                  </label>
                  <input
                    type="text"
                    className={`input input-bordered input-lg w-full text-lg ${
                      !formData.title[selectedLanguage]?.trim()
                        ? 'input-error'
                        : ''
                    }`}
                    value={formData.title[selectedLanguage] || ''}
                    onChange={(e) =>
                      handleLanguageFieldChange(
                        'title',
                        selectedLanguage,
                        e.target.value,
                      )
                    }
                    placeholder={`Enter title in ${
                      selectedLanguage === Language.EN ? 'English' : 'Spanish'
                    }`}
                  />
                </div>

                {/* Description Input */}
                <div className="form-control">
                  <label className="label">
                    <span className="label-text text-base font-semibold">
                      Description ({selectedLanguage.toUpperCase()})
                    </span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered input-lg w-full text-lg"
                    value={formData.description[selectedLanguage] || ''}
                    onChange={(e) =>
                      handleLanguageFieldChange(
                        'description',
                        selectedLanguage,
                        e.target.value,
                      )
                    }
                    placeholder={`Brief description in ${
                      selectedLanguage === Language.EN ? 'English' : 'Spanish'
                    }`}
                  />
                </div>
              </div>

              {/* Content Editor - Full Height */}
              <div className="form-control min-h-0 flex-1">
                <div className="flex items-center justify-between">
                  <label className="label">
                    <span className="label-text text-base font-semibold">
                      Content ({selectedLanguage.toUpperCase()})
                    </span>
                    <span className="label-text-alt text-error font-medium">
                      {!formData.content[selectedLanguage]?.trim()
                        ? 'Required'
                        : ''}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setMediaLibraryContext('content');
                      setShowMediaLibrary(true);
                    }}
                    title="Click to browse media library. Images will be inserted at cursor position or set as thumbnail."
                  >
                    📂 Media Library
                  </button>
                </div>
                <div
                  className={`flex-1 rounded-lg border bg-white pb-10 ${
                    !formData.content[selectedLanguage]?.trim()
                      ? 'border-error'
                      : 'border-base-300'
                  }`}
                >
                  <ReactQuill
                    key={selectedLanguage} // Add key to force re-render when language changes
                    ref={quillRef}
                    theme="snow"
                    value={formData.content[selectedLanguage] || ''}
                    onChange={handleContentChange}
                    modules={stableModules}
                    formats={stableFormats}
                    className="h-auto min-h-[60vh]"
                    placeholder={`Write your content in ${
                      selectedLanguage === Language.EN ? 'English' : 'Spanish'
                    }... Use the toolbar above to format text, add headers, lists, links, and images.`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Settings Sidebar */}
          <div className="border-base-300 flex w-72 flex-col overflow-hidden border-l bg-white">
            {/* Settings Tabs */}
            <div className="border-base-300 flex border-b bg-white px-4 pb-2 pt-4">
              <div className="tabs tabs-boxed tabs-sm w-full">
                <button
                  className={`tab tab-sm flex-1 ${
                    activeTab === 'content' ? 'tab-active' : ''
                  }`}
                  onClick={() => setActiveTab('content')}
                >
                  <span className="mr-1">⚙️</span>
                  Settings
                </button>
                <button
                  className={`tab tab-sm flex-1 ${
                    activeTab === 'meta' ? 'tab-active' : ''
                  }`}
                  onClick={() => setActiveTab('meta')}
                >
                  <span className="mr-1">🔗</span>
                  URLs
                </button>
                <button
                  className={`tab tab-sm flex-1 ${
                    activeTab === 'seo' ? 'tab-active' : ''
                  }`}
                  onClick={() => setActiveTab('seo')}
                >
                  <span className="mr-1">🎯</span>
                  SEO
                </button>
              </div>
            </div>

            {/* Settings Content */}
            <div className="flex-1 overflow-y-auto bg-white p-4">
              {activeTab === 'content' && (
                <div className="space-y-5">
                  {/* Content Type */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">
                        Content Type
                      </span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      value={formData.type}
                      onChange={(e) =>
                        handleFieldChange('type', e.target.value)
                      }
                    >
                      <option value="blog">📝 Blog Post</option>
                      <option value="document">📄 Documentation</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">
                        Publication Status
                      </span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      value={formData.status}
                      onChange={(e) =>
                        handleFieldChange('status', e.target.value)
                      }
                    >
                      <option value="draft">📝 Draft</option>
                      <option value="published">✅ Published</option>
                      <option value="archived">📦 Archived</option>
                    </select>
                  </div>

                  {/* Author */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">Author</span>
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      value={formData.author}
                      onChange={(e) =>
                        handleFieldChange('author', e.target.value)
                      }
                      placeholder="Author name"
                    />
                  </div>

                  {/* Thumbnail */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">
                        Thumbnail Image
                      </span>
                    </label>

                    {/* URL Input */}
                    <div className="input-group mb-3">
                      <input
                        type="url"
                        className="input input-bordered flex-1"
                        value={formData.thumbnail}
                        onChange={(e) =>
                          handleFieldChange('thumbnail', e.target.value)
                        }
                        placeholder="https://example.com/image.jpg"
                      />
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => {
                          setMediaLibraryContext('thumbnail');
                          setShowMediaLibrary(true);
                        }}
                        disabled={isUploadingThumbnail}
                      >
                        📂 Library
                      </button>
                    </div>

                    {/* Upload Button */}
                    <div className="flex gap-2">
                      <label className="btn btn-primary btn-sm flex-1 cursor-pointer">
                        {isUploadingThumbnail ? (
                          <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Uploading...
                          </>
                        ) : (
                          <>📤 Upload Image</>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleThumbnailUpload(file);
                            }
                          }}
                          disabled={isUploadingThumbnail}
                        />
                      </label>
                      {formData.thumbnail && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleFieldChange('thumbnail', '')}
                        >
                          🗑️ Remove
                        </button>
                      )}
                    </div>

                    {/* Preview */}
                    {formData.thumbnail && (
                      <div className="mt-3">
                        <img
                          key={formData.thumbnail}
                          src={`${formData.thumbnail}${
                            formData.thumbnail.includes('?') ? '&' : '?'
                          }v=${Date.now()}`}
                          alt="Thumbnail preview"
                          className="border-base-300 h-24 w-full rounded-lg border object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">🏷️ Tags</span>
                    </label>
                    <div className="space-y-3">
                      <div>
                        <label className="label-text text-sm">
                          English Tags
                        </label>
                        <input
                          type="text"
                          className="input input-bordered w-full"
                          value={formData.tags.en?.join(', ') || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              tags: {
                                ...prev.tags,
                                en: e.target.value
                                  .split(',')
                                  .map((t) => t.trim())
                                  .filter((t) => t),
                              },
                            }))
                          }
                          placeholder="tag1, tag2, tag3"
                        />
                      </div>
                      <div>
                        <label className="label-text text-sm">
                          Spanish Tags
                        </label>
                        <input
                          type="text"
                          className="input input-bordered w-full"
                          value={formData.tags.es?.join(', ') || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              tags: {
                                ...prev.tags,
                                es: e.target.value
                                  .split(',')
                                  .map((t) => t.trim())
                                  .filter((t) => t),
                              },
                            }))
                          }
                          placeholder="etiqueta1, etiqueta2, etiqueta3"
                        />
                      </div>
                    </div>
                    <div className="label">
                      <span className="label-text-alt text-base-content/60">
                        Separate with commas
                      </span>
                    </div>
                  </div>

                  {/* Featured Toggle */}
                  <div className="form-control">
                    <label className="label cursor-pointer">
                      <span className="label-text font-semibold">
                        <span className="mr-2">⭐</span>
                        Featured Content
                      </span>
                      <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={formData.featured}
                        onChange={(e) =>
                          handleFieldChange('featured', e.target.checked)
                        }
                      />
                    </label>
                    <div className="label">
                      <span className="label-text-alt text-base-content/60">
                        Featured content appears prominently on the website
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'meta' && (
                <div className="space-y-5">
                  <div className="alert alert-info">
                    <svg
                      className="h-5 w-5 shrink-0 stroke-current"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                    <span className="text-sm">
                      URL slugs should be URL-friendly (lowercase, no spaces)
                    </span>
                  </div>

                  {/* English Slug */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">
                        URL Slug (English)
                      </span>
                    </label>
                    <div className="input-group">
                      <span className="bg-base-200 text-base-content/60 px-3 text-sm">
                        /en/
                      </span>
                      <input
                        type="text"
                        className="input input-bordered flex-1"
                        value={formData.slug.en || ''}
                        onChange={(e) =>
                          handleLanguageFieldChange(
                            'slug',
                            Language.EN,
                            e.target.value,
                          )
                        }
                        placeholder="url-friendly-slug"
                      />
                    </div>
                  </div>

                  {/* Spanish Slug */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-semibold">
                        URL Slug (Spanish)
                      </span>
                    </label>
                    <div className="input-group">
                      <span className="bg-base-200 text-base-content/60 px-3 text-sm">
                        /es/
                      </span>
                      <input
                        type="text"
                        className="input input-bordered flex-1"
                        value={formData.slug.es || ''}
                        onChange={(e) =>
                          handleLanguageFieldChange(
                            'slug',
                            Language.ES,
                            e.target.value,
                          )
                        }
                        placeholder="slug-amigable-url"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'seo' && (
                <div className="space-y-5">
                  <div className="alert alert-warning">
                    <svg
                      className="h-5 w-5 shrink-0 stroke-current"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.866-.833-2.598 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      ></path>
                    </svg>
                    <div className="text-sm">
                      <div className="font-semibold">SEO Optimization</div>
                      <div>
                        Keep titles under 60 characters and descriptions under
                        160
                      </div>
                    </div>
                  </div>

                  {/* English SEO */}
                  <div className="space-y-4">
                    <h4 className="text-base-content flex items-center font-semibold">
                      <span className="mr-2">🇺🇸</span>
                      English SEO
                    </h4>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">
                          Meta Title
                        </span>
                        <span className="label-text-alt">
                          {formData.seo.metaTitle.en?.length || 0}/60
                        </span>
                      </label>
                      <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={60}
                        value={formData.seo.metaTitle.en || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            seo: {
                              ...prev.seo,
                              metaTitle: {
                                ...prev.seo.metaTitle,
                                en: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder="SEO title in English"
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">
                          Meta Description
                        </span>
                        <span className="label-text-alt">
                          {formData.seo.metaDescription.en?.length || 0}/160
                        </span>
                      </label>
                      <textarea
                        className="textarea textarea-bordered w-full resize-none"
                        rows={3}
                        maxLength={160}
                        value={formData.seo.metaDescription.en || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            seo: {
                              ...prev.seo,
                              metaDescription: {
                                ...prev.seo.metaDescription,
                                en: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder="SEO description in English"
                      />
                    </div>
                  </div>

                  {/* Spanish SEO */}
                  <div className="space-y-4">
                    <h4 className="text-base-content flex items-center font-semibold">
                      <span className="mr-2">🇪🇸</span>
                      Spanish SEO
                    </h4>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">
                          Meta Title
                        </span>
                        <span className="label-text-alt">
                          {formData.seo.metaTitle.es?.length || 0}/60
                        </span>
                      </label>
                      <input
                        type="text"
                        className="input input-bordered w-full"
                        maxLength={60}
                        value={formData.seo.metaTitle.es || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            seo: {
                              ...prev.seo,
                              metaTitle: {
                                ...prev.seo.metaTitle,
                                es: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder="Título SEO en español"
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">
                          Meta Description
                        </span>
                        <span className="label-text-alt">
                          {formData.seo.metaDescription.es?.length || 0}/160
                        </span>
                      </label>
                      <textarea
                        className="textarea textarea-bordered w-full resize-none"
                        rows={3}
                        maxLength={160}
                        value={formData.seo.metaDescription.es || ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            seo: {
                              ...prev.seo,
                              metaDescription: {
                                ...prev.seo.metaDescription,
                                es: e.target.value,
                              },
                            },
                          }))
                        }
                        placeholder="Descripción SEO en español"
                      />
                    </div>
                  </div>

                  {/* Additional SEO Fields */}
                  <div className="mt-6 space-y-4">
                    <h4 className="text-base-content flex items-center font-semibold">
                      <span className="mr-2">🔗</span>
                      Additional SEO Settings
                    </h4>

                    {/* Canonical URLs */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            Canonical URL (EN)
                          </span>
                        </label>
                        <input
                          type="url"
                          className="input input-bordered w-full"
                          value={formData.seo.canonicalUrl.en || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                canonicalUrl: {
                                  ...prev.seo.canonicalUrl,
                                  en: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder="https://example.com/page"
                        />
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            Canonical URL (ES)
                          </span>
                        </label>
                        <input
                          type="url"
                          className="input input-bordered w-full"
                          value={formData.seo.canonicalUrl.es || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                canonicalUrl: {
                                  ...prev.seo.canonicalUrl,
                                  es: e.target.value,
                                },
                              },
                            }))
                          }
                          placeholder="https://example.com/es/page"
                        />
                      </div>
                    </div>

                    {/* Keywords */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            Keywords (EN)
                          </span>
                        </label>
                        <input
                          type="text"
                          className="input input-bordered w-full"
                          value={formData.seo.keywords.en?.join(', ') || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                keywords: {
                                  ...prev.seo.keywords,
                                  en: e.target.value
                                    .split(',')
                                    .map((k) => k.trim())
                                    .filter((k) => k),
                                },
                              },
                            }))
                          }
                          placeholder="keyword1, keyword2, keyword3"
                        />
                        <div className="label">
                          <span className="label-text-alt">
                            Separate with commas
                          </span>
                        </div>
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            Keywords (ES)
                          </span>
                        </label>
                        <input
                          type="text"
                          className="input input-bordered w-full"
                          value={formData.seo.keywords.es?.join(', ') || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                keywords: {
                                  ...prev.seo.keywords,
                                  es: e.target.value
                                    .split(',')
                                    .map((k) => k.trim())
                                    .filter((k) => k),
                                },
                              },
                            }))
                          }
                          placeholder="palabra1, palabra2, palabra3"
                        />
                        <div className="label">
                          <span className="label-text-alt">
                            Separar con comas
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Open Graph Settings */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            OG Image URL
                          </span>
                        </label>
                        <input
                          type="url"
                          className="input input-bordered w-full"
                          value={formData.seo.ogImage || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                ogImage: e.target.value,
                              },
                            }))
                          }
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-medium">
                            OG Type
                          </span>
                        </label>
                        <select
                          className="select select-bordered w-full"
                          value={formData.seo.ogType || 'article'}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              seo: {
                                ...prev.seo,
                                ogType: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="article">Article</option>
                          <option value="website">Website</option>
                          <option value="blog">Blog</option>
                          <option value="product">Product</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer (sticky) */}
        <div className="border-base-300 bg-base-100 sticky bottom-0 z-10 flex items-center justify-between border-t px-6 py-4 shadow-lg">
          <div className="text-base-content/60 flex items-center gap-2 text-sm">
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Content will be saved in both languages</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="btn btn-ghost"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary btn-lg"
              disabled={
                isSaving ||
                !formData.title.en.trim() ||
                !formData.content.en.trim()
              }
            >
              {isSaving ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Save Content
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Modal backdrop */}
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose} disabled={isSaving}>
          close
        </button>
      </form>

      {/* Media Library Modal */}
      <MediaLibraryComponent
        agentId={agentId}
        onSelectMedia={handleMediaSelect}
        isOpen={showMediaLibrary}
        onClose={() => setShowMediaLibrary(false)}
      />
    </div>
  );
};
